// @ts-nocheck
/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { Block } from './types';
import { useEditorCtx } from './BlogEditor';

/* ── Strip Google Docs / Word CSS on paste, preserve semantic structure ── */
function handleCleanPaste(e: React.ClipboardEvent) {
  e.preventDefault();
  const html = e.clipboardData.getData('text/html');
  if (html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    tmp.querySelectorAll('meta,style,script,head').forEach(el => el.remove());
    tmp.querySelectorAll('[style],[class],[id]').forEach(el => {
      el.removeAttribute('style');
      el.removeAttribute('class');
      el.removeAttribute('id');
    });
    tmp.querySelectorAll('span').forEach(span => span.replaceWith(...Array.from(span.childNodes)));
    document.execCommand('insertHTML', false, tmp.innerHTML);
  } else {
    document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
  }
}

/* ── inline markdown + passthrough HTML → rendered HTML ── */
function renderInline(text: string): string {
  return text
    // Images first (before link rule so ![…](…) doesn't partially match)
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width:100%;height:auto;display:block;margin:var(--seasons-space-6) 0;" />')
    // Markdown links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    // Markdown bold / italic (only when not already inside an HTML tag)
    .replace(/\*\*(.+?)\*\*/gs, '<strong>$1</strong>')
    .replace(/__(.+?)__/gs, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/gs, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>');
  // NOTE: We intentionally do NOT escape & < > here because content
  // imported from Webflow already contains real HTML tags (<strong>,
  // <em>, <br>, &nbsp; etc.) that must be rendered, not shown as text.
}

/* ── Paste sanitizer for contentEditable blocks ──
 * Browsers hand raw clipboard HTML to contentEditable on paste. If that HTML
 * carries block-level elements (nested headings, divs), editor-only attributes
 * (data-placeholder) or clipboard fragment comments — e.g. from selecting text
 * that spans two adjacent editor fields — it gets saved as the block's `text`
 * and rendered unescaped on the public page, producing invalid nested markup
 * like <h3><h4 data-placeholder="…">…</h4></h3>. Strip to a safe inline subset
 * before insertion so only text + intentional bold/italic/links/line-breaks survive.
 */
const PASTE_ALLOWED_INLINE = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'A', 'BR', 'SUP', 'SUB']);
const PASTE_BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'UL', 'OL', 'SECTION', 'ARTICLE', 'BLOCKQUOTE', 'TABLE', 'TR', 'TD', 'TH', 'FIGURE', 'FIGCAPTION']);

export function sanitizePastedHtml(html: string, multiline: boolean): string {
  const escape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const doc = new DOMParser().parseFromString(html, 'text/html');

  function walk(node: Node): string {
    let out = '';
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += escape(child.textContent || '');
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as Element;
        const tag = el.tagName;
        if (tag === 'BR') { out += '<br>'; return; }
        const inner = walk(el);
        if (PASTE_ALLOWED_INLINE.has(tag)) {
          if (tag === 'A') {
            const href = el.getAttribute('href') || '';
            out += href ? `<a href="${escape(href)}" target="_blank" rel="noopener noreferrer">${inner}</a>` : inner;
          } else {
            out += `<${tag.toLowerCase()}>${inner}</${tag.toLowerCase()}>`;
          }
        } else if (tag === 'SPAN' || tag === 'FONT') {
          // Preserve only a text color, if present (e.g. from the toolbar's
          // color picker) — nothing else about the span/font wrapper.
          const raw = tag === 'FONT' ? el.getAttribute('color') : (el.getAttribute('style')?.match(/color:\s*([^;]+)/i)?.[1]);
          const color = raw && /^#[0-9a-f]{3,8}$|^rgb\(|^[a-z]+$/i.test(raw.trim()) ? raw.trim() : null;
          out += color ? `<span style="color:${escape(color)}">${inner}</span>` : inner;
        } else {
          // Drop the wrapper (and any of its attributes, e.g. data-placeholder,
          // id, class) but keep the text content. Block-level elements get a
          // line break after so pasted paragraphs/headings don't run together.
          out += inner;
          if (PASTE_BLOCK_TAGS.has(tag)) out += '<br>';
        }
      }
      // Comment nodes (e.g. <!--StartFragment-->) are skipped entirely.
    });
    return out;
  }

  let result = walk(doc.body).replace(/^(<br>)+/, '').replace(/(<br>)+$/, '');
  if (!multiline) result = result.replace(/<br>/g, ' ');
  return result;
}

/** Shrinks a Range inward past any leading/trailing whitespace, in place.
 * Double-clicking a word to select it often includes a trailing (sometimes
 * leading) space in the Range. That space sits outside the formatting tag,
 * so queryCommandState('bold') reports the selection as "not fully bold"
 * and a second click re-applies bold to the whole thing (including the
 * space) instead of toggling it off — the word looks unchanged either way,
 * so the toggle appears to silently do nothing. */
function trimRangeWhitespace(range: Range): void {
  const text = range.toString();
  const trailing = text.length - text.replace(/\s+$/, '').length;
  for (let i = 0; i < trailing && range.endOffset > 0; i++) {
    range.setEnd(range.endContainer, range.endOffset - 1);
  }
  const leading = text.match(/^\s+/)?.[0].length ?? 0;
  for (let i = 0; i < leading; i++) {
    range.setStart(range.startContainer, range.startOffset + 1);
  }
}

/** Custom, weight-independent bold check. `document.queryCommandState('bold')`
 * decides "is this bold?" from the selection's *effective computed weight*
 * (≥ ~600 in Chromium) — so any context that intentionally renders <b>/
 * <strong> below that (e.g. a 500-weight title, to avoid a too-heavy look)
 * makes the browser permanently think the text isn't bold, and a second
 * click re-applies bold instead of removing it. This checks DOM ancestry
 * instead, so it works regardless of the rendered weight. */
function isBoldSelection(sel: Selection | null): boolean {
  if (!sel || !sel.rangeCount) return false;
  const range = sel.getRangeAt(0);
  const elFor = (n: Node) => (n instanceof Element ? n : n.parentElement);
  const startsBold = !!elFor(range.startContainer)?.closest('b, strong');
  const endsBold = !!elFor(range.endContainer)?.closest('b, strong');
  return startsBold && endsBold;
}

/** Manually unwrap <b>/<strong> intersecting a range, then re-select the
 * unwrapped content. Used instead of execCommand('bold') for the "turn bold
 * off" direction, since that command shares the same weight-based detection
 * as queryCommandState and silently no-ops (or re-bolds) once it thinks the
 * text isn't bold.
 *
 * Operates on the live <b>/<strong> elements directly rather than via
 * Range.extractContents() — extractContents on a range that exactly spans
 * one text node's full content (the common case: re-selecting a word that
 * IS the entire content of its <b>) only extracts the text *data*, per
 * spec, leaving the <b> element itself untouched in the live DOM. Reinserting
 * that extracted text then just puts it right back inside the same
 * (still-present) <b> — a silent no-op that looks like the toggle "worked"
 * (selection/DOM structure both round-trip) but never actually removes the
 * wrapper. */
function unwrapBold(sel: Selection, range: Range): void {
  const elFor = (n: Node) => (n instanceof Element ? n : n.parentElement);
  const candidates = new Set<Element>();
  const startAnc = elFor(range.startContainer)?.closest('b, strong');
  const endAnc = elFor(range.endContainer)?.closest('b, strong');
  if (startAnc) candidates.add(startAnc);
  if (endAnc) candidates.add(endAnc);
  elFor(range.commonAncestorContainer)?.querySelectorAll('b, strong').forEach((el) => {
    if (range.intersectsNode(el)) candidates.add(el);
  });
  if (!candidates.size) return;

  let firstNode: ChildNode | null = null;
  let lastNode: ChildNode | null = null;
  candidates.forEach((el) => {
    const parent = el.parentNode;
    if (!parent) return;
    const kids = Array.from(el.childNodes);
    kids.forEach((child) => parent.insertBefore(child, el));
    parent.removeChild(el);
    if (kids.length) {
      firstNode = firstNode ?? kids[0];
      lastNode = kids[kids.length - 1];
    }
  });

  if (firstNode && lastNode) {
    const newRange = document.createRange();
    newRange.setStartBefore(firstNode);
    newRange.setEndAfter(lastNode);
    sel.removeAllRanges();
    sel.addRange(newRange);
  }
}

/* ── Floating rich-text format toolbar ── */
function FormatToolbar() {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [fmt, setFmt] = useState({ bold: false, italic: false, underline: false, strike: false, superscript: false, subscript: false });
  const [noBold, setNoBold] = useState(false);
  const [noLink, setNoLink] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const savedRangeRef = useRef<Range | null>(null);

  // Re-reads the live selection into `fmt`/`pos`. Used both by the
  // `selectionchange` listener below and right after execCommand calls,
  // since mutating the DOM via execCommand doesn't reliably fire
  // `selectionchange` afterward — without an immediate refresh, the
  // toolbar's active-highlight goes stale, and (more importantly) a
  // stale/lost selection means a second click meant to toggle a format
  // back off can silently act on nothing, forcing Undo instead.
  const refresh = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.rangeCount) { setPos(null); return; }
    const anchor = sel.anchorNode;
    const el = (anchor instanceof Element ? anchor : anchor?.parentElement)?.closest('[contenteditable="true"]');
    if (!el) { setPos(null); return; }
    // Opt out via data-no-format — e.g. speaker name/title/org, which are
    // meant to stay plain, short identity fields rather than rich text.
    if (el.closest('[data-no-format]')) { setPos(null); return; }
    // Trim boundary whitespace (e.g. from double-clicking a word) so the
    // active-highlight reflects the selection execCommand will actually see.
    const range = sel.getRangeAt(0);
    trimRangeWhitespace(range);
    if (range.collapsed) { setPos(null); return; }
    sel.removeAllRanges();
    sel.addRange(range);
    const r = range.getBoundingClientRect();
    if (!r.width) { setPos(null); return; }
    setPos({ top: r.top - 48, left: r.left + r.width / 2 });
    // Opt out of just the Bold button via data-no-bold — e.g. resource
    // titles, whose base weight (500) is already the same as the bold
    // override, so a "Bold" button there would visibly do nothing.
    setNoBold(!!el.closest('[data-no-bold]'));
    // Opt out of just the Link button via data-no-link — titles/headings
    // across Blogs, Resources, Webinars shouldn't link out from the
    // article's own name (other formatting stays available there).
    setNoLink(!!el.closest('[data-no-link]'));
    setFmt({
      bold: isBoldSelection(sel),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strike: document.queryCommandState('strikeThrough'),
      superscript: document.queryCommandState('superscript'),
      subscript: document.queryCommandState('subscript'),
    });
  };

  useEffect(() => {
    document.addEventListener('selectionchange', refresh);
    return () => document.removeEventListener('selectionchange', refresh);
  }, []);

  if (!pos || typeof document === 'undefined') return null;

  const apply = (cmd: string, val?: string) => {
    const sel = window.getSelection();
    const savedRange = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    if (savedRange) trimRangeWhitespace(savedRange);
    if (cmd === 'bold' && sel && savedRange && isBoldSelection(sel)) {
      // Turning bold off: don't hand this to execCommand('bold') — it uses
      // the same weight-based "is this bold?" check as queryCommandState,
      // so in a context that renders bold below ~600 (e.g. a 500-weight
      // title) it thinks the text still isn't bold and re-applies instead
      // of removing. Unwrap the <b>/<strong> ourselves instead.
      sel.removeAllRanges();
      sel.addRange(savedRange);
      unwrapBold(sel, savedRange);
    } else {
      document.execCommand(cmd, false, val);
      // execCommand can collapse/shift the selection — restore it so a second
      // click (toggling the format back off) has a valid, non-collapsed
      // selection to act on instead of silently no-oping.
      if (sel && savedRange && sel.isCollapsed) {
        sel.removeAllRanges();
        sel.addRange(savedRange);
      }
    }
    refresh();
  };

  const btn = (label: string, cmd: string, active: boolean, title: string, val?: string) => (
    <button
      className={`be-format-btn${active ? ' is-active' : ''}`}
      onMouseDown={(e) => { e.preventDefault(); apply(cmd, val); }}
      title={title}
    >{label}</button>
  );

  const applyColor = (color: string) => {
    const sel = window.getSelection();
    if (sel && savedRangeRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
    // Scope styleWithCSS to just this one command — leaving it on would make
    // bold/italic etc. emit <span style="..."> instead of <strong>/<em>,
    // breaking the paste sanitizer's tag allowlist and the "bold-only
    // paragraph → subheading" CSS detection elsewhere in the app.
    document.execCommand('styleWithCSS', false, true);
    document.execCommand('foreColor', false, color);
    document.execCommand('styleWithCSS', false, false);
    refresh();
  };

  return createPortal(
    <div
      ref={toolbarRef}
      className="be-format-toolbar"
      style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-50%)' }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {!noBold && btn('B', 'bold', fmt.bold, 'Bold (⌘B)')}
      {btn('I', 'italic', fmt.italic, 'Italic (⌘I)')}
      {btn('U', 'underline', fmt.underline, 'Underline (⌘U)')}
      {btn('S', 'strikeThrough', fmt.strike, 'Strikethrough')}
      <div className="be-format-btn--sep" />
      {btn('x²', 'superscript', fmt.superscript, 'Superscript')}
      {btn('x₂', 'subscript', fmt.subscript, 'Subscript')}
      {!noLink && (
        <>
          <div className="be-format-btn--sep" />
          <button
            className="be-format-btn"
            title="Link (⌘K)"
            onMouseDown={(e) => {
              e.preventDefault();
              const url = window.prompt('URL:');
              if (url) apply('createLink', url);
            }}
          >🔗</button>
        </>
      )}
      <button
        className="be-format-btn"
        title="Inline code"
        onMouseDown={(e) => {
          e.preventDefault();
          const sel = window.getSelection();
          if (!sel || sel.isCollapsed) return;
          const text = sel.toString();
          document.execCommand('insertHTML', false, `<code>${text}</code>`);
        }}
      >{`</>`}</button>
      <div className="be-format-btn--sep" />
      <button
        className="be-format-btn"
        title="Text color"
        style={{ position: 'relative' }}
        onMouseDown={(e) => {
          e.preventDefault();
          const sel = window.getSelection();
          savedRangeRef.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
          colorInputRef.current?.click();
        }}
      >
        A
        <input
          ref={colorInputRef}
          type="color"
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
          onChange={(e) => applyColor(e.target.value)}
        />
      </button>
      <div className="be-format-btn--sep" />
      <button
        className="be-format-btn"
        title="Clear formatting"
        onMouseDown={(e) => { e.preventDefault(); apply('removeFormat'); }}
      >Tx</button>
    </div>,
    document.body,
  );
}
export { FormatToolbar };

/* ── Editable text with contentEditable ── */
function EditableText({
  tag: Tag = 'p',
  value,
  placeholder,
  className,
  style,
  multiline = false,
  id,
  onSave,
}: {
  tag?: keyof JSX.IntrinsicElements;
  value: string;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  multiline?: boolean;
  id?: string;
  onSave: (val: string) => void;
}) {
  const { editMode } = useEditorCtx();
  const ref = useRef<HTMLElement>(null);

  // Sync value → DOM only when not focused (avoid fighting contentEditable)
  useEffect(() => {
    if (!ref.current || document.activeElement === ref.current) return;
    ref.current.innerHTML = value;
  }, [value, editMode]);

  if (!editMode) {
    return (
      <Tag
        id={id}
        className={className}
        style={style}
        dangerouslySetInnerHTML={{ __html: renderInline(value) }}
      />
    );
  }

  return (
    <Tag
      id={id}
      ref={ref as any}
      className={`be-text-editable${className ? ' ' + className : ''}`}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder}
      style={style}
      onBlur={(e) => onSave((e.currentTarget as HTMLElement).innerHTML)}
      onPaste={(e) => {
        e.preventDefault();
        const html = e.clipboardData.getData('text/html');
        let inserted: string;
        if (html) {
          inserted = sanitizePastedHtml(html, multiline);
        } else {
          const escaped = e.clipboardData.getData('text/plain')
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          inserted = multiline ? escaped.replace(/\r?\n/g, '<br>') : escaped.replace(/\r?\n/g, ' ');
        }
        document.execCommand('insertHTML', false, inserted);
      }}
      onKeyDown={(e) => {
        if (!multiline && e.key === 'Enter') { e.preventDefault(); (e.currentTarget as HTMLElement).blur(); }
        if (e.metaKey || e.ctrlKey) {
          if (e.key === 'b') { e.preventDefault(); document.execCommand('bold', false); }
          if (e.key === 'i') { e.preventDefault(); document.execCommand('italic', false); }
          if (e.key === 'u') { e.preventDefault(); document.execCommand('underline', false); }
          if (e.key === 'k') {
            e.preventDefault();
            const url = window.prompt('URL:');
            if (url) document.execCommand('createLink', false, url);
          }
        }
      }}
    />
  );
}

/* ── Upload zone ── */
function UploadZone({
  src, accept, onChange, aspect = '16/9', placeholder,
}: {
  src?: string;
  accept: string;
  onChange: (url: string) => void;
  aspect?: string;
  placeholder?: string;
}) {
  const { editMode, uploadFile } = useEditorCtx();
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  const upload = async (file: File) => {
    const url = await uploadFile(file);
    onChange(url);
  };

  if (!editMode) {
    return src ? (
      accept.startsWith('video') ? (
        <video src={src} controls style={{ width: '100%', display: 'block' }} />
      ) : (
        <img src={src} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} alt="" />
      )
    ) : null;
  }

  return (
    <div
      className={`be-upload-zone${drag ? ' is-drag-over' : ''}`}
      style={{ aspectRatio: aspect }}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) upload(f); }}
    >
      {src ? (
        accept.startsWith('video') ? (
          <video src={src} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <img src={src} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        )
      ) : (
        <>
          <svg width="28" height="28" fill="currentColor" viewBox="0 0 256 256" style={{ opacity: 0.4 }} aria-hidden="true"><path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,200V172l52-52,44,44a8,8,0,0,0,11.31,0l24-24,36.07,36.06L216,200Zm140-128a12,12,0,1,1-12-12A12,12,0,0,1,180,72Z"/></svg>
          <span style={{ fontSize: 13 }}>{placeholder || 'Click or drop to upload'}</span>
        </>
      )}
      <div className="be-upload-zone__overlay">
        <svg width="20" height="20" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M213.66,82.34l-56-56a8,8,0,0,0-11.32,0l-96,96A8,8,0,0,0,48,128v56a8,8,0,0,0,8,8h56a8,8,0,0,0,5.66-2.34l96-96A8,8,0,0,0,213.66,82.34ZM108.69,176H64V131.31l72-72L180.69,104Zm84-84L147.31,47.31,168,26.62,202.69,61.31Z"/></svg>
        <span>Replace</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────
   ResizableMediaWrapper — drag handle on the right edge
   ───────────────────────────────────────────── */
function ResizableMediaWrapper({
  width,
  align,
  editMode,
  onWidthChange,
  children,
  className,
  style,
}: {
  width?: string;
  align?: string;
  editMode: boolean;
  onWidthChange: (w: string) => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  const outerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const justifyMap: Record<string, string> = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };
  const justify = justifyMap[align || 'left'] || 'flex-start';

  const outerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: justify,
    width: '100%',
    ...style,
  };

  const innerStyle: React.CSSProperties = {
    width: width || '100%',
    flexShrink: 0,
    position: 'relative',
  };

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    const outerW = outerRef.current?.getBoundingClientRect().width || 1;
    startW.current = containerRef.current?.getBoundingClientRect().width || outerW;

    const onMouseMove = (ev: MouseEvent) => {
      if (!dragging.current) return;
      const outerWidth = outerRef.current?.getBoundingClientRect().width || 1;
      const delta = ev.clientX - startX.current;
      const rawPct = ((startW.current + delta) / outerWidth) * 100;
      const clamped = Math.max(10, Math.min(200, Math.round(rawPct / 5) * 5));
      onWidthChange(`${clamped}%`);
    };

    const onMouseUp = () => {
      dragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div ref={outerRef} className="be-resizable-outer" style={outerStyle}>
      <div
        ref={containerRef}
        className={`be-resizable${className ? ' ' + className : ''}`}
        style={innerStyle}
      >
        {children}
        {editMode && (
          <div className="be-resize-handle" onMouseDown={onMouseDown} />
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   AlignedPreviewWrapper — flex-based alignment for preview mode
   (same logic as ResizableMediaWrapper outer div, but no resize handle)
   ───────────────────────────────────────────── */
function AlignedPreviewWrapper({
  width,
  align,
  children,
  className,
  style,
}: {
  width?: string;
  align?: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  if (!width) return <>{children}</>;
  const justifyMap: Record<string, string> = { left: 'flex-start', center: 'center', right: 'flex-end' };
  const justify = justifyMap[align || 'left'] || 'flex-start';
  return (
    <div style={{ display: 'flex', justifyContent: justify, width: '100%' }}>
      <div style={{ width, flexShrink: 0, ...style }} className={className}>
        {children}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MediaSizeToolbar — width, align, and aspect ratio presets
   ───────────────────────────────────────────── */
function MediaSizeToolbar({
  width,
  align,
  aspectRatio,
  onWidthChange,
  onAlignChange,
  onAspectChange,
  showAspect,
}: {
  width?: string;
  align?: string;
  aspectRatio?: string;
  onWidthChange: (w: string) => void;
  onAlignChange: (a: string) => void;
  onAspectChange?: (ar: string) => void;
  showAspect?: boolean;
}) {
  const widthPresets = [
    { label: '¼', value: '25%' },
    { label: '½', value: '50%' },
    { label: '¾', value: '75%' },
    { label: 'Full', value: '100%' },
  ];
  const aspectPresets = ['16/9', '4/3', '3/2', '1/1', '3/4', 'auto'];

  return (
    <div className="be-media-toolbar">
      <div className="be-media-toolbar__group">
        <span className="be-media-toolbar__label">Width</span>
        {widthPresets.map((p) => (
          <button
            key={p.value}
            className={`be-media-preset-btn${width === p.value ? ' is-active' : ''}`}
            onClick={() => onWidthChange(p.value)}
            type="button"
          >
            {p.label}
          </button>
        ))}
        <input
          className="be-media-width-input"
          value={width || '100%'}
          onChange={(e) => onWidthChange(e.target.value)}
          placeholder="100%"
        />
      </div>
      {width && (
        <>
          <div className="be-media-toolbar__sep" />
          <div className="be-media-toolbar__group">
            <span className="be-media-toolbar__label">Align</span>
            {(['left', 'center', 'right'] as const).map((a) => (
              <button
                key={a}
                type="button"
                className={`be-media-preset-btn${align === a ? ' is-active' : ''}`}
                onClick={() => onAlignChange(a)}
                title={a}
              >
                {a === 'left' ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="1" y="2" width="12" height="2" rx="1"/><rect x="1" y="6" width="8" height="2" rx="1"/><rect x="1" y="10" width="10" height="2" rx="1"/></svg>
                ) : a === 'center' ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="1" y="2" width="12" height="2" rx="1"/><rect x="3" y="6" width="8" height="2" rx="1"/><rect x="2" y="10" width="10" height="2" rx="1"/></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="1" y="2" width="12" height="2" rx="1"/><rect x="5" y="6" width="8" height="2" rx="1"/><rect x="3" y="10" width="10" height="2" rx="1"/></svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
      {showAspect && onAspectChange && (
        <>
          <div className="be-media-toolbar__sep" />
          <div className="be-media-toolbar__group">
            <span className="be-media-toolbar__label">Ratio</span>
            {aspectPresets.map((ar) => (
              <button
                key={ar}
                type="button"
                className={`be-media-preset-btn${(aspectRatio || 'auto') === ar ? ' is-active' : ''}`}
                onClick={() => onAspectChange(ar === 'auto' ? '' : ar)}
              >
                {ar}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Individual block renderers (use `any` casts to avoid
   TypeScript's union-too-complex error on the Block type)
   ───────────────────────────────────────────── */

function ParagraphBlock({ block }: { block: any }) {
  const { updateBlock } = useEditorCtx();
  return (
    <EditableText
      tag="p"
      className="blog-mdx-p"
      value={block.text}
      placeholder="Start writing…"
      multiline
      onSave={(v) => updateBlock(block.id, { text: v })}
    />
  );
}

function LedeBlock({ block }: { block: any }) {
  const { updateBlock } = useEditorCtx();
  return (
    <div style={{ display: 'contents' }}>
      <EditableText
        tag="p"
        value={block.text}
        placeholder="Lead paragraph…"
        className="type-prose-body-lg blog-prose-lede"
        multiline
        onSave={(v) => updateBlock(block.id, { text: v })}
      />
    </div>
  );
}

function HeadingBlock({ block }: { block: any }) {
  const { updateBlock } = useEditorCtx();
  return (
    <EditableText
      tag={block.type as 'h2' | 'h3' | 'h4'}
      id={block.type !== 'h4' ? `h-${block.id}` : undefined}
      value={block.text}
      placeholder={`${block.type.toUpperCase()} heading`}
      onSave={(v) => updateBlock(block.id, { text: v })}
    />
  );
}

function BlockquoteBlock({ block }: { block: any }) {
  const { updateBlock } = useEditorCtx();
  return (
    <blockquote>
      <EditableText
        tag="p"
        value={block.text}
        placeholder="Blockquote text…"
        multiline
        onSave={(v) => updateBlock(block.id, { text: v })}
      />
    </blockquote>
  );
}

function PullquoteBlock({ block }: { block: any }) {
  const { updateBlock, editMode } = useEditorCtx();
  return (
    <div style={{ margin: 'var(--seasons-space-prose-subsection) 0' }}>
      <EditableText
        tag="p"
        value={block.quote}
        placeholder="Pull quote text…"
        className="blog-pullquote"
        multiline
        style={{ marginBottom: 8 }}
        onSave={(v) => updateBlock(block.id, { quote: v })}
      />
      {(block.cite || editMode) && (
        <EditableText
          tag="cite"
          value={block.cite || ''}
          placeholder="Attribution"
          style={{ display: 'block', fontSize: 'var(--seasons-font-size-14)', color: 'var(--seasons-color-prose-meta)', fontStyle: 'normal', paddingLeft: 8 }}
          onSave={(v) => updateBlock(block.id, { cite: v })}
        />
      )}
    </div>
  );
}

function RichListItem({ value, bullet, onChange, onAddAfter, onRemove }: {
  value: string; bullet: string;
  onChange: (v: string) => void; onAddAfter: () => void; onRemove: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) ref.current.innerHTML = value;
  }, [value]);
  return (
    <div className="be-list-item-row">
      <span className="be-list-item-bullet">{bullet}</span>
      <div
        ref={ref}
        className="be-list-item-input be-text-editable"
        contentEditable
        suppressContentEditableWarning
        style={{ outline: 'none', flex: 1, minHeight: '1.2em', background: 'transparent', color: 'inherit', fontFamily: 'inherit', fontSize: 'inherit' }}
        onBlur={(e) => onChange(e.currentTarget.innerHTML)}
        onPaste={handleCleanPaste}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); onAddAfter(); }
          if (e.key === 'Backspace' && (ref.current?.textContent || '') === '') { e.preventDefault(); onRemove(); }
        }}
      />
    </div>
  );
}

function ListBlock({ block }: { block: any }) {
  const { updateBlock, editMode, richText } = useEditorCtx();
  const items = block.items || [''];

  const update = (newItems: string[]) => updateBlock(block.id, { items: newItems });

  if (!editMode) {
    const Tag = block.type;
    return (
      <Tag>
        {items.map((item, i) => (
          // Wrap in <span> so inline elements (e.g. <strong>) inside the item
          // don't become separate flex items when .wp2-left-body li { display: flex }
          // is in effect — without the wrapper each <strong> gets gap applied to it.
          <li key={i}>
            <span dangerouslySetInnerHTML={{ __html: renderInline(item) }} />
          </li>
        ))}
      </Tag>
    );
  }

  if (richText) {
    return (
      <div className="be-list-editor" style={{ padding: '4px 0' }}>
        {items.map((item, i) => (
          <RichListItem
            key={i}
            value={item}
            bullet={block.type === 'ol' ? `${i + 1}.` : '•'}
            onChange={(v) => { const copy = [...items]; copy[i] = v; update(copy); }}
            onAddAfter={() => { const copy = [...items]; copy.splice(i + 1, 0, ''); update(copy); }}
            onRemove={() => { if (items.length > 1) update(items.filter((_, idx) => idx !== i)); }}
          />
        ))}
        <button className="be-add-btn" style={{ marginTop: 6, fontSize: 12 }} onClick={() => update([...items, ''])}>+ Add item</button>
      </div>
    );
  }

  return (
    <div className="be-list-editor" style={{ padding: '4px 0' }}>
      {items.map((item, i) => (
        <div key={i} className="be-list-item-row">
          <span className="be-list-item-bullet">{block.type === 'ol' ? `${i + 1}.` : '•'}</span>
          <input
            className="be-list-item-input"
            value={item}
            onChange={(e) => { const copy = [...items]; copy[i] = e.target.value; update(copy); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const copy = [...items];
                copy.splice(i + 1, 0, '');
                update(copy);
                // Focus next
                setTimeout(() => {
                  const inputs = document.querySelectorAll<HTMLInputElement>(`[data-listid="${block.id}"]`);
                  inputs[i + 1]?.focus();
                }, 10);
              }
              if (e.key === 'Backspace' && item === '' && items.length > 1) {
                e.preventDefault();
                const copy = items.filter((_, idx) => idx !== i);
                update(copy);
              }
            }}
            data-listid={block.id}
            placeholder={`Item ${i + 1}`}
          />
          {items.length > 1 && (
            <button
              className="be-list-item-del"
              onClick={() => update(items.filter((_, idx) => idx !== i))}
              aria-label="Remove item"
            >×</button>
          )}
        </div>
      ))}
      <button
        className="be-add-btn"
        style={{ marginTop: 6, fontSize: 12 }}
        onClick={() => update([...items, ''])}
      >
        + Add item
      </button>
    </div>
  );
}

function CodeBlock({ block }: { block: any }) {
  const { updateBlock, editMode } = useEditorCtx();

  if (!editMode) {
    return (
      <pre data-no-listen="true"><code>{block.code}</code></pre>
    );
  }

  return (
    <div className="be-code-editor" data-no-listen="true">
      <div className="be-code-header">
        <input
          className="be-code-lang"
          value={block.language || ''}
          onChange={(e) => updateBlock(block.id, { language: e.target.value })}
          placeholder="Language (e.g. javascript)"
        />
      </div>
      <textarea
        className="be-code-textarea"
        value={block.code}
        onChange={(e) => updateBlock(block.id, { code: e.target.value })}
        placeholder="Paste or type code here…"
        spellCheck={false}
      />
    </div>
  );
}

function HrBlock() {
  return <hr />;
}

function BlogImageBlock({ block }: { block: any }) {
  const { updateBlock, editMode } = useEditorCtx();
  const [lightbox, setLightbox] = useState(false);

  return (
    <>
      <ResizableMediaWrapper
        width={block.width}
        align={block.align}
        editMode={editMode}
        onWidthChange={(w) => updateBlock(block.id, { width: w })}
      >
      <figure data-no-listen="true" className={`blog-image${block.breakout ? ' blog-image--breakout' : ''}${editMode ? ' be-structured-block' : ''}`}>
        <div className="blog-image__media" style={{ cursor: block.src && !editMode ? 'zoom-in' : undefined, aspectRatio: block.aspectRatio || undefined }} onClick={() => { if (!editMode && block.src) setLightbox(true); }}>
          <UploadZone
            src={block.src}
            accept="image/*"
            onChange={(url) => updateBlock(block.id, { src: url })}
            placeholder="Click to upload image"
          />
        </div>
        {editMode ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              className="be-field__input"
              value={block.caption || ''}
              onChange={(e) => updateBlock(block.id, { caption: e.target.value })}
              placeholder="Caption (optional)"
              style={{ flex: '1 1 200px', fontSize: 13 }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--seasons-color-text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
              <input type="checkbox" checked={!!block.breakout} onChange={(e) => updateBlock(block.id, { breakout: e.target.checked })} />
              Breakout
            </label>
            <input
              type="text"
              className="be-field__input"
              value={block.alt || ''}
              onChange={(e) => updateBlock(block.id, { alt: e.target.value })}
              placeholder="Alt text"
              style={{ flex: '1 1 150px', fontSize: 13 }}
            />
            <MediaSizeToolbar
              width={block.width}
              align={block.align}
              aspectRatio={block.aspectRatio}
              onWidthChange={(w) => updateBlock(block.id, { width: w })}
              onAlignChange={(a) => updateBlock(block.id, { align: a })}
              onAspectChange={(ar) => updateBlock(block.id, { aspectRatio: ar })}
              showAspect
            />
          </div>
        ) : block.caption ? (
          <figcaption className="blog-image__caption">{block.caption}</figcaption>
        ) : null}
      </figure>
      </ResizableMediaWrapper>
      {/* Lightbox */}
      {lightbox && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={() => setLightbox(false)}
        >
          <button
            onClick={() => setLightbox(false)}
            style={{ position: 'absolute', top: 16, right: 20, background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: 40, height: 40, borderRadius: '50%', cursor: 'pointer', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            aria-label="Close"
          >×</button>
          <img
            src={block.src}
            alt={block.alt || block.caption || ''}
            style={{ maxWidth: '95vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 24px 80px rgba(0,0,0,0.5)' }}
            onClick={(e) => e.stopPropagation()}
          />
          {block.caption && (
            <p style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.7)', fontSize: 13, margin: 0, textAlign: 'center', maxWidth: 600 }}>{block.caption}</p>
          )}
        </div>
      )}
    </>
  );
}

function BlogVideoBlock({ block }: { block: any }) {
  const { updateBlock, editMode } = useEditorCtx();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [duration, setDuration] = useState('0:00');
  const [muted, setMuted] = useState(false);

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const toggleFullscreen = () => {
    const w = videoRef.current?.parentElement;
    if (!w) return;
    if (!document.fullscreenElement) w.requestFullscreen?.();
    else document.exitFullscreen?.();
  };
  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    v.currentTime = ((e.clientX - rect.left) / rect.width) * v.duration;
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      const d = v.duration || 0;
      setProgress(d > 0 ? (v.currentTime / d) * 100 : 0);
      setCurrentTime(fmt(v.currentTime));
    };
    const onMeta = () => setDuration(fmt(v.duration));
    const onEnded = () => setPlaying(false);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('ended', onEnded);
    return () => { v.removeEventListener('timeupdate', onTime); v.removeEventListener('loadedmetadata', onMeta); v.removeEventListener('ended', onEnded); };
  }, [block.src]);

  if (editMode) {
    return (
      <ResizableMediaWrapper
        width={block.width}
        align={block.align}
        editMode={editMode}
        onWidthChange={(w) => updateBlock(block.id, { width: w })}
      >
      <figure data-no-listen="true" className={`blog-video${block.breakout ? ' blog-video--breakout' : ''} be-structured-block`}>
        <div className="blog-video__wrap">
          <UploadZone
            src={block.src}
            accept="video/*"
            onChange={(url) => updateBlock(block.id, { src: url })}
            placeholder="Click to upload video"
            aspect={block.aspectRatio || '16/9'}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="be-field__input"
            value={block.caption || ''}
            onChange={(e) => updateBlock(block.id, { caption: e.target.value })}
            placeholder="Caption"
            style={{ flex: '1 1 200px', fontSize: 13 }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--seasons-color-text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
            <input type="checkbox" checked={!!block.breakout} onChange={(e) => updateBlock(block.id, { breakout: e.target.checked })} />
            Breakout
          </label>
          <MediaSizeToolbar
            width={block.width}
            align={block.align}
            aspectRatio={block.aspectRatio}
            onWidthChange={(w) => updateBlock(block.id, { width: w })}
            onAlignChange={(a) => updateBlock(block.id, { align: a })}
            onAspectChange={(ar) => updateBlock(block.id, { aspectRatio: ar })}
            showAspect
          />
        </div>
      </figure>
      </ResizableMediaWrapper>
    );
  }

  return (
    <AlignedPreviewWrapper width={block.width} align={block.align}>
    <figure
      data-no-listen="true"
      className={`blog-video${block.breakout ? ' blog-video--breakout' : ''}`}
      style={block.width ? { width: '100%' } : undefined}
    >
      <div className={`blog-video__wrap${playing ? ' blog-video__wrap--playing' : ''}`}>
        {block.src ? (
          <>
            <video
              ref={videoRef}
              src={block.src}
              poster={block.poster}
              className="blog-video__el"
              playsInline
              style={{ width: '100%', display: 'block' }}
            />
            {!playing && (
              <button type="button" className="blog-video__play-overlay" onClick={togglePlay} aria-label="Play video">
                <span className="blog-video__play-overlay-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" viewBox="0 0 256 256"><path d="M240,128a15.74,15.74,0,0,1-7.6,13.51L88.32,229.65a16,16,0,0,1-16.2.3A15.86,15.86,0,0,1,64,216.13V39.87a15.86,15.86,0,0,1,8.12-13.82,16,16,0,0,1,16.2.3L232.4,114.49A15.74,15.74,0,0,1,240,128Z"/></svg>
                </span>
              </button>
            )}
            <div className="blog-video__controls">
              <button className="blog-video__ctrl" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
                {playing
                  ? <svg width="14" height="14" fill="currentColor" viewBox="0 0 256 256"><path d="M216,48V208a16,16,0,0,1-16,16H160a16,16,0,0,1-16-16V48a16,16,0,0,1,16-16h40A16,16,0,0,1,216,48ZM96,32H56A16,16,0,0,0,40,48V208a16,16,0,0,0,16,16H96a16,16,0,0,0,16-16V48A16,16,0,0,0,96,32Z"/></svg>
                  : <svg width="14" height="14" fill="currentColor" viewBox="0 0 256 256"><path d="M240,128a15.74,15.74,0,0,1-7.6,13.51L88.32,229.65a16,16,0,0,1-16.2.3A15.86,15.86,0,0,1,64,216.13V39.87a15.86,15.86,0,0,1,8.12-13.82,16,16,0,0,1,16.2.3L232.4,114.49A15.74,15.74,0,0,1,240,128Z"/></svg>
                }
              </button>
              <span className="blog-video__time">{currentTime}<span className="blog-video__time-sep"> / </span>{duration}</span>
              <div className="blog-video__progress" role="slider" tabIndex={0} aria-label="Seek" onClick={seek}>
                <span className="blog-video__progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <button className="blog-video__ctrl" onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>
                {muted
                  ? <svg width="14" height="14" fill="currentColor" viewBox="0 0 256 256"><path d="M53.92,34.62A8,8,0,1,0,42.08,45.38L73.55,80H32a16,16,0,0,0-16,16v64a16,16,0,0,0,16,16H76.69L120,219.31A16,16,0,0,0,144,208V131.09l58.08,63.31A8,8,0,1,0,213.92,184l-160-160ZM128,208l-43.31-43.31A8,8,0,0,0,79,163H32V96H79a8,8,0,0,0,5.66-2.34L128,50ZM155.51,54.47A8,8,0,0,1,166,43.93C212.92,77.17,231.32,130.48,232,128a8,8,0,0,1-15.93,1.56C215,123.41,197.43,80,166,57.83A8,8,0,0,1,155.51,54.47Z"/></svg>
                  : <svg width="14" height="14" fill="currentColor" viewBox="0 0 256 256"><path d="M155.51,24.81a8,8,0,0,0-8.42.88L77.25,80H32A16,16,0,0,0,16,96v64a16,16,0,0,0,16,16H77.25l69.84,54.31A8,8,0,0,0,160,224V32A8,8,0,0,0,155.51,24.81ZM144,207.64l-57.68-44.84A8,8,0,0,0,81.25,160H32V96H81.25a8,8,0,0,0,5.07-1.8L144,49.64Zm48-106.84a40,40,0,0,1,0,54.4,8,8,0,0,1-11.32-11.31,24,24,0,0,0,0-31.78A8,8,0,1,1,192,100.8Z"/></svg>
                }
              </button>
              <button className="blog-video__ctrl" onClick={toggleFullscreen} aria-label="Fullscreen">
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 256 256"><path d="M216,48V96a8,8,0,0,1-16,0V67.31l-42.34,42.35a8,8,0,0,1-11.32-11.32L188.69,56H160a8,8,0,0,1,0-16h48A8,8,0,0,1,216,48ZM98.34,146.34,56,188.69V160a8,8,0,0,0-16,0v48a8,8,0,0,0,8,8H96a8,8,0,0,0,0-16H67.31l42.35-42.34a8,8,0,0,0-11.32-11.32Z"/></svg>
              </button>
            </div>
          </>
        ) : (
          <div style={{ aspectRatio: '16/9', background: 'var(--seasons-color-surface-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, color: 'var(--seasons-color-text-tertiary)', fontSize: 14 }}>No video</div>
        )}
      </div>
      {block.caption && <figcaption className="blog-video__caption">{block.caption}</figcaption>}
    </figure>
    </AlignedPreviewWrapper>
  );
}

function ImageCarouselBlock({ block }: { block: any }) {
  const { updateBlock, editMode, uploadFile } = useEditorCtx();
  const images = block.images || [];
  const slideInputRef = useRef<HTMLInputElement>(null);

  const addSlide = async (file: File) => {
    const url = await uploadFile(file);
    updateBlock(block.id, { images: [...images, { src: url, alt: '' }] });
  };

  const [currentSlide, setCurrentSlide] = useState(0);
  const goTo = (i: number) => setCurrentSlide(Math.max(0, Math.min(i, images.length - 1)));

  if (!editMode) {
    return (
      <AlignedPreviewWrapper width={block.width} align={block.align}>
      <figure
        data-no-listen="true"
        className={`blog-image-carousel${block.filmstrip ? ' blog-image-carousel--filmstrip' : ''}`}
        style={block.width ? { width: '100%' } : undefined}
      >
        <div className="blog-image-carousel__viewport">
          <div className="blog-image-carousel__track" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
            {images.map((img, i) => (
              <div key={i} className="blog-image-carousel__slide" aria-hidden={i !== currentSlide}>
                {img.src && <img src={img.src} alt={img.alt || ''} />}
              </div>
            ))}
          </div>
        </div>
        {images.length > 1 && (
          <>
            <div className="blog-image-carousel__nav">
              <button type="button" className="btn-icon-only btn-icon-only--sm btn-icon-only--outline" aria-label="Previous image" onClick={() => goTo(currentSlide - 1)} disabled={currentSlide === 0}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 256 256"><path d="M165.66,202.34a8,8,0,0,1-11.32,11.32l-80-80a8,8,0,0,1,0-11.32l80-80a8,8,0,0,1,11.32,11.32L91.31,128Z"/></svg>
              </button>
              <button type="button" className="btn-icon-only btn-icon-only--sm btn-icon-only--outline" aria-label="Next image" onClick={() => goTo(currentSlide + 1)} disabled={currentSlide === images.length - 1}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 256 256"><path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"/></svg>
              </button>
            </div>
            <div className="blog-image-carousel__dots">
              {images.map((_, i) => (
                <button key={i} type="button" className={`blog-image-carousel__dot${i === currentSlide ? ' blog-image-carousel__dot--active' : ''}`} onClick={() => goTo(i)} aria-label={`Slide ${i + 1}`} />
              ))}
            </div>
          </>
        )}
        {block.caption && <figcaption className="blog-image-carousel__caption">{block.caption}</figcaption>}
      </figure>
      </AlignedPreviewWrapper>
    );
  }

  return (
    <ResizableMediaWrapper
      width={block.width}
      align={block.align}
      editMode={editMode}
      onWidthChange={(w) => updateBlock(block.id, { width: w })}
    >
    <figure data-no-listen="true" className={`blog-image-carousel${block.filmstrip ? ' blog-image-carousel--filmstrip' : ''} be-structured-block`} style={{ padding: '12px', border: '1px dashed var(--seasons-color-border-strong)', borderRadius: 'var(--seasons-radius-md)' }}>
      <p style={{ fontSize: 12, color: 'var(--seasons-color-text-placeholder)', margin: '0 0 10px', fontFamily: 'var(--seasons-font-polysans, system-ui)' }}>Image Carousel ({images.length} slides)</p>
      <div className="be-carousel-slides">
        {images.map((img, i) => (
          <div key={i} className="be-carousel-slide-thumb">
            {img.src && <img src={img.src} alt={img.alt} />}
            <button
              className="be-carousel-slide-del"
              onClick={() => updateBlock(block.id, { images: images.filter((_, idx) => idx !== i) })}
              aria-label="Remove slide"
            >×</button>
          </div>
        ))}
        <button className="be-carousel-slide-add" onClick={() => slideInputRef.current?.click()} aria-label="Add slide">+</button>
        <input ref={slideInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
          onChange={async (e) => {
            const files = Array.from(e.target.files || []);
            const urls = await Promise.all(files.map(f => uploadFile(f)));
            updateBlock(block.id, { images: [...images, ...urls.map(u => ({ src: u, alt: '' }))] });
            e.target.value = '';
          }} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="be-field__input"
          value={block.caption || ''}
          onChange={(e) => updateBlock(block.id, { caption: e.target.value })}
          placeholder="Caption"
          style={{ flex: '1 1 200px', fontSize: 13 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--seasons-color-text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
          <input type="checkbox" checked={!!block.filmstrip} onChange={(e) => updateBlock(block.id, { filmstrip: e.target.checked })} />
          Filmstrip variant
        </label>
        <MediaSizeToolbar
          width={block.width}
          align={block.align}
          onWidthChange={(w) => updateBlock(block.id, { width: w })}
          onAlignChange={(a) => updateBlock(block.id, { align: a })}
        />
      </div>
    </figure>
    </ResizableMediaWrapper>
  );
}

function ImageGridBlock({ block }: { block: any }) {
  const { updateBlock, editMode, uploadFile } = useEditorCtx();
  const images = block.images || [];
  const gridInputRef = useRef<HTMLInputElement>(null);

  if (!editMode) {
    return (
      <AlignedPreviewWrapper width={block.width} align={block.align}>
      <figure
        data-no-listen="true"
        className={`blog-image-grid${block.breakout ? ' blog-image-grid--breakout' : ''}`}
        style={block.width ? { width: '100%' } : undefined}
      >
        <div className="blog-image-grid__items" style={{ gridTemplateColumns: `repeat(${Math.min(images.length, 3)}, 1fr)` }}>
          {images.map((img, i) => (
            <div key={i} className="blog-image-grid__cell">
              {img.src && <img src={img.src} alt={img.alt || ''} className="blog-image-grid__el" />}
              {img.caption && <span className="blog-image-grid__cell-caption">{img.caption}</span>}
            </div>
          ))}
        </div>
      </figure>
      </AlignedPreviewWrapper>
    );
  }

  return (
    <ResizableMediaWrapper
      width={block.width}
      align={block.align}
      editMode={editMode}
      onWidthChange={(w) => updateBlock(block.id, { width: w })}
    >
    <figure data-no-listen="true" className={`blog-image-grid${block.breakout ? ' blog-image-grid--breakout' : ''} be-structured-block`} style={{ padding: '12px', border: '1px dashed var(--seasons-color-border-strong)', borderRadius: 'var(--seasons-radius-md)' }}>
      <p style={{ fontSize: 12, color: 'var(--seasons-color-text-placeholder)', margin: '0 0 10px', fontFamily: 'var(--seasons-font-polysans, system-ui)' }}>Image Grid ({images.length} images)</p>
      <div className="be-carousel-slides">
        {images.map((img, i) => (
          <div key={i} className="be-carousel-slide-thumb">
            {img.src && <img src={img.src} alt={img.alt} />}
            <button className="be-carousel-slide-del" onClick={() => updateBlock(block.id, { images: images.filter((_, idx) => idx !== i) })} aria-label="Remove">×</button>
          </div>
        ))}
        <button className="be-carousel-slide-add" onClick={() => gridInputRef.current?.click()}>+</button>
        <input ref={gridInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
          onChange={async (e) => {
            const files = Array.from(e.target.files || []);
            const urls = await Promise.all(files.map(f => uploadFile(f)));
            updateBlock(block.id, { images: [...images, ...urls.map(u => ({ src: u, alt: '' }))] });
            e.target.value = '';
          }} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--seasons-color-text-secondary)', cursor: 'pointer', marginTop: 10 }}>
        <input type="checkbox" checked={!!block.breakout} onChange={(e) => updateBlock(block.id, { breakout: e.target.checked })} />
        Breakout (full width)
      </label>
      <MediaSizeToolbar
        width={block.width}
        align={block.align}
        onWidthChange={(w) => updateBlock(block.id, { width: w })}
        onAlignChange={(a) => updateBlock(block.id, { align: a })}
      />
    </figure>
    </ResizableMediaWrapper>
  );
}

function CalloutBlock({ block }: { block: any }) {
  const { updateBlock, editMode } = useEditorCtx();
  const variant = block.variant || 'default';
  const cls = `blog-callout${variant === 'note' ? ' blog-callout--note' : variant === 'warning' ? ' blog-callout--warning' : ''}`;

  const InfoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">
      <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm16-40a8,8,0,0,1-8,8,16,16,0,0,1-16-16V128a8,8,0,0,1,0-16,16,16,0,0,1,16,16v40A8,8,0,0,1,144,176ZM112,84a12,12,0,1,1,12,12A12,12,0,0,1,112,84Z"/>
    </svg>
  );
  const WarnIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">
      <path d="M236.8,188.09,149.35,36.22a24.76,24.76,0,0,0-42.7,0L19.2,188.09a23.51,23.51,0,0,0,0,23.72A24.35,24.35,0,0,0,40.55,224h174.9a24.35,24.35,0,0,0,21.33-12.19A23.51,23.51,0,0,0,236.8,188.09ZM120,104a8,8,0,0,1,16,0v40a8,8,0,0,1-16,0Zm8,88a12,12,0,1,1,12-12A12,12,0,0,1,128,192Z"/>
    </svg>
  );

  return (
    <div className={cls}>
      {editMode && (
        <div className="be-callout-variant-selector" style={{ marginBottom: 8 }}>
          {(['default', 'note', 'warning'] as const).map(v => (
            <button key={v} className={`be-callout-variant-btn${variant === v ? ' is-active' : ''}`} onClick={() => updateBlock(block.id, { variant: v })}>
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      )}
      <span className="blog-callout__icon">{variant === 'warning' ? <WarnIcon /> : <InfoIcon />}</span>
      <div className="blog-callout__body">
        <EditableText
          tag="div"
          value={block.title || ''}
          placeholder="Callout title"
          className="blog-callout__title"
          onSave={(v) => updateBlock(block.id, { title: v })}
        />
        <EditableText
          tag="p"
          value={block.children || ''}
          placeholder="Callout body text…"
          multiline
          onSave={(v) => updateBlock(block.id, { children: v })}
        />
      </div>
    </div>
  );
}

function ReadingCalloutBlock({ block }: { block: any }) {
  const { updateBlock, editMode } = useEditorCtx();
  return (
    <aside data-no-listen="true" className="blog-reading-callout">
      <EditableText
        tag="p"
        value={block.eyebrow || ''}
        placeholder="Eyebrow (e.g. Related from Innovaccer)"
        className="blog-reading-callout__eyebrow"
        onSave={(v) => updateBlock(block.id, { eyebrow: v })}
      />
      <EditableText
        tag="p"
        value={block.body || ''}
        placeholder="Callout body text…"
        className="blog-reading-callout__body"
        multiline
        onSave={(v) => updateBlock(block.id, { body: v })}
      />
      {editMode ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <input
            className="be-field__input"
            value={block.linkLabel || ''}
            onChange={(e) => updateBlock(block.id, { linkLabel: e.target.value })}
            placeholder="Link label"
            style={{ flex: '1 1 120px', fontSize: 13 }}
          />
          <input
            className="be-field__input"
            value={block.linkUrl || ''}
            onChange={(e) => updateBlock(block.id, { linkUrl: e.target.value })}
            placeholder="Link URL"
            style={{ flex: '2 1 200px', fontSize: 13 }}
          />
        </div>
      ) : block.linkUrl ? (
        <a className="text-link" href={block.linkUrl}>
          <span className="text-link__label">{block.linkLabel || 'Read more'}</span>
        </a>
      ) : null}
    </aside>
  );
}

function BlogTableBlock({ block }: { block: any }) {
  const { updateBlock, editMode } = useEditorCtx();
  const headers = block.headers || [];
  const rows = block.rows || [];

  const updateHeader = (i: number, val: string) => {
    const copy = [...headers]; copy[i] = val;
    updateBlock(block.id, { headers: copy });
  };
  const updateCell = (ri: number, ci: number, val: string) => {
    const copy = rows.map(r => ({ cells: [...(r.cells || [])] }));
    if (!copy[ri]) copy[ri] = { cells: [] };
    copy[ri].cells[ci] = val;
    updateBlock(block.id, { rows: copy });
  };
  const addCol = () => {
    updateBlock(block.id, {
      headers: [...headers, `Column ${headers.length + 1}`],
      rows: rows.map(r => ({ cells: [...(r.cells || []), ''] })),
    });
  };
  const addRow = () => updateBlock(block.id, { rows: [...rows, { cells: headers.map(() => '') }] });
  const removeRow = (i: number) => updateBlock(block.id, { rows: rows.filter((_, idx) => idx !== i) });
  const removeCol = (i: number) => {
    updateBlock(block.id, {
      headers: headers.filter((_, idx) => idx !== i),
      rows: rows.map(r => ({ cells: (r.cells || []).filter((_, idx) => idx !== i) })),
    });
  };

  return (
    <div data-no-listen="true" className={`blog-table-wrap${block.highlightLast ? ' blog-table-wrap--highlight-last' : ''}`}>
      <div className="be-table-editor">
        <table>
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i}>
                  {editMode ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input
                        className="be-table-cell-input"
                        value={h}
                        onChange={(e) => updateHeader(i, e.target.value)}
                      />
                      {headers.length > 1 && editMode && (
                        <button style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--seasons-color-text-placeholder)', fontSize: 12, padding: 0, flexShrink: 0 }} onClick={() => removeCol(i)} title="Remove column">×</button>
                      )}
                    </div>
                  ) : <span dangerouslySetInnerHTML={{ __html: renderInline(h) }} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {(row.cells || []).map((cell, ci) => (
                  <td key={ci}>
                    {editMode ? (
                      <input
                        className="be-table-cell-input"
                        value={cell}
                        onChange={(e) => updateCell(ri, ci, e.target.value)}
                      />
                    ) : <span dangerouslySetInnerHTML={{ __html: renderInline(cell) }} />}
                  </td>
                ))}
                {editMode && (
                  <td style={{ width: 28, padding: 4 }}>
                    <button style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--seasons-color-text-placeholder)', fontSize: 14, padding: 0 }} onClick={() => removeRow(ri)} title="Remove row">×</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editMode && (
        <div className="be-table-actions">
          <button className="be-add-btn" onClick={addRow} style={{ fontSize: 12 }}>+ Row</button>
          <button className="be-add-btn" onClick={addCol} style={{ fontSize: 12 }}>+ Column</button>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--seasons-color-text-secondary)', cursor: 'pointer', marginLeft: 'auto' }}>
            <input type="checkbox" checked={!!block.highlightLast} onChange={(e) => updateBlock(block.id, { highlightLast: e.target.checked })} />
            Highlight last col
          </label>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   COLUMNS BLOCK — supports image / video / icon / text side-by-side
   ───────────────────────────────────────────── */
function ColumnsBlock({ block }: { block: any }) {
  const { updateBlock, editMode, uploadFile } = useEditorCtx();
  const cols = block.cols || 2;
  const items: any[] = block.items || [];

  const updateItem = (idx: number, patch: any) => {
    const next = items.map((it, i) => i === idx ? { ...it, ...patch } : it);
    updateBlock(block.id, { items: next });
  };
  const setKind = (idx: number, kind: string) => updateItem(idx, { kind, src: '', text: '', alt: '', caption: '' });
  const setCols = (n: 2 | 3) => {
    let next = [...items];
    if (n > items.length) while (next.length < n) next.push({ kind: 'image' });
    if (n < items.length) next = next.slice(0, n);
    updateBlock(block.id, { cols: n, items: next });
  };

  return (
    <figure data-no-listen="true" className={`blog-columns blog-columns--${cols}${block.breakout ? ' blog-columns--breakout' : ''}${editMode ? ' be-structured-block' : ''}`}>
      {editMode && (
        <div className="be-columns-toolbar">
          <span style={{ fontSize: 12, color: 'var(--seasons-color-text-placeholder)' }}>Columns layout</span>
          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            <button className={`be-callout-variant-btn${cols === 2 ? ' is-active' : ''}`} onClick={() => setCols(2)}>2 cols</button>
            <button className={`be-callout-variant-btn${cols === 3 ? ' is-active' : ''}`} onClick={() => setCols(3)}>3 cols</button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--seasons-color-text-secondary)', cursor: 'pointer', marginLeft: 8 }}>
              <input type="checkbox" checked={!!block.breakout} onChange={(e) => updateBlock(block.id, { breakout: e.target.checked })} />
              Breakout
            </label>
          </div>
        </div>
      )}
      <div className="blog-columns__grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 24, alignItems: 'start' }}>
        {items.map((it, idx) => (
          <div key={idx} className="blog-columns__cell">
            {editMode && (
              <div className="be-columns-cell-toolbar">
                {(['image', 'video', 'icon', 'text'] as const).map(k => (
                  <button
                    key={k}
                    className={`be-callout-variant-btn${it.kind === k ? ' is-active' : ''}`}
                    onClick={() => setKind(idx, k)}
                    style={{ fontSize: 11, padding: '2px 8px' }}
                  >{k}</button>
                ))}
              </div>
            )}
            {it.kind === 'image' && (
              <UploadZone
                src={it.src}
                accept="image/*"
                onChange={(url) => updateItem(idx, { src: url })}
                placeholder="Image"
                aspect="4/3"
              />
            )}
            {it.kind === 'video' && (
              <UploadZone
                src={it.src}
                accept="video/*"
                onChange={(url) => updateItem(idx, { src: url })}
                placeholder="Video"
                aspect="4/3"
              />
            )}
            {it.kind === 'icon' && (
              <div className="blog-columns__icon-wrap">
                {it.src ? (
                  <img src={it.src} alt={it.alt || ''} style={{ width: it.size || 64, height: it.size || 64, objectFit: 'contain' }} />
                ) : editMode ? (
                  <UploadZone src={it.src} accept="image/*,image/svg+xml" onChange={(url) => updateItem(idx, { src: url })} placeholder="Icon" aspect="1/1" />
                ) : null}
              </div>
            )}
            {it.kind === 'text' && (
              editMode ? (
                <textarea
                  className="be-code-textarea"
                  value={it.text || ''}
                  onChange={(e) => updateItem(idx, { text: e.target.value })}
                  placeholder="Text content (markdown supported)…"
                  style={{ minHeight: 100, fontFamily: 'var(--seasons-font-polysans, system-ui)', fontSize: 15, padding: 12, background: 'transparent', border: '1px dashed var(--seasons-color-border-strong)', borderRadius: 6 }}
                />
              ) : (
                <div className="blog-columns__text" dangerouslySetInnerHTML={{ __html: renderInline(it.text || '') }} />
              )
            )}
            {editMode && (it.kind === 'image' || it.kind === 'video') && (
              <input
                className="be-field__input"
                value={it.caption || ''}
                onChange={(e) => updateItem(idx, { caption: e.target.value })}
                placeholder="Caption (optional)"
                style={{ marginTop: 6, fontSize: 12 }}
              />
            )}
            {!editMode && (it.kind === 'image' || it.kind === 'video') && it.caption && (
              <span className="blog-columns__caption">{it.caption}</span>
            )}
          </div>
        ))}
      </div>
      {editMode ? (
        <input
          className="be-field__input"
          value={block.caption || ''}
          onChange={(e) => updateBlock(block.id, { caption: e.target.value })}
          placeholder="Group caption (optional)"
          style={{ marginTop: 8, fontSize: 13 }}
        />
      ) : block.caption ? (
        <figcaption className="blog-columns__group-caption">{block.caption}</figcaption>
      ) : null}
    </figure>
  );
}

/* ─────────────────────────────────────────────
   STATS GRID
   ───────────────────────────────────────────── */
function StatsGridBlock({ block }: { block: any }) {
  const { updateBlock, editMode } = useEditorCtx();
  const items: Array<{ value: string; label: string }> = block.items || [];
  const variant: string = block.variant || 'default';
  const variantClass = variant === 'brand' ? ' blog-stats-grid--brand' : variant === 'dark' ? ' blog-stats-grid--dark' : '';

  if (editMode) {
    return (
      <div className={`blog-stats-grid${variantClass} be-structured-block`}>
        {items.map((item, idx) => (
          <div key={idx} className="blog-stats-grid__item">
            <input
              className="blog-stats-grid__value"
              value={item.value || ''}
              onChange={(e) => {
                const updated = [...items];
                updated[idx] = { ...item, value: e.target.value };
                updateBlock(block.id, { items: updated });
              }}
              placeholder="Value (e.g. 93%)"
              style={{ background: 'transparent', border: 'none', outline: 'none', width: '100%', fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', letterSpacing: 'inherit', lineHeight: 'inherit' }}
            />
            <input
              className="blog-stats-grid__label"
              value={item.label || ''}
              onChange={(e) => {
                const updated = [...items];
                updated[idx] = { ...item, label: e.target.value };
                updateBlock(block.id, { items: updated });
              }}
              placeholder="Label"
              style={{ background: 'transparent', border: 'none', outline: 'none', width: '100%', fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`blog-stats-grid${variantClass}`}>
      {items.map((item, idx) => (
        <div key={idx} className="blog-stats-grid__item">
          <div className="blog-stats-grid__value">{item.value}</div>
          <div className="blog-stats-grid__label">{item.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   AUTHOR CARD
   ───────────────────────────────────────────── */
function AuthorCardBlock({ block }: { block: any }) {
  const { updateBlock, editMode } = useEditorCtx();

  return (
    <div data-no-listen="true" className={`blog-author-card${editMode ? ' be-structured-block' : ''}`}>
      {/* Avatar — only rendered in read mode when a URL is set */}
      {!editMode && block.avatar && (
        <img
          className="blog-author-card__avatar"
          src={block.avatar}
          alt={block.name || 'Author'}
          width={64}
          height={64}
        />
      )}
      {editMode && (
        <input
          className="be-field__input"
          value={block.avatar || ''}
          onChange={(e) => updateBlock(block.id, { avatar: e.target.value })}
          placeholder="Avatar image URL"
          style={{ marginBottom: 6, fontSize: 12 }}
        />
      )}
      <div className="blog-author-card__body">
        <p className="blog-author-card__name">
          {editMode ? (
            <EditableText
              tag="span"
              value={block.name || ''}
              placeholder="Author name"
              onSave={(v) => updateBlock(block.id, { name: v })}
            />
          ) : block.profileUrl ? (
            <a href={block.profileUrl} className="blog-author-card__name-link">{block.name || ''}</a>
          ) : (
            block.name || ''
          )}
        </p>
        <EditableText
          tag="p"
          className="blog-author-card__role"
          value={block.role || ''}
          placeholder="Title or role"
          onSave={(v) => updateBlock(block.id, { role: v })}
        />
        <EditableText
          tag="p"
          className="blog-author-card__bio"
          value={block.bio || ''}
          placeholder="Short bio…"
          multiline
          onSave={(v) => updateBlock(block.id, { bio: v })}
        />
        {editMode && (
          <input
            className="be-field__input"
            value={block.profileUrl || ''}
            onChange={(e) => updateBlock(block.id, { profileUrl: e.target.value })}
            placeholder="Profile URL (wraps author name as a link)"
            style={{ marginTop: 6, fontSize: 12 }}
          />
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   INLINE CTA (dark band)
   ───────────────────────────────────────────── */
function InlineCTABlock({ block }: { block: any }) {
  const { updateBlock, editMode } = useEditorCtx();
  return (
    <div data-no-listen="true" className={`blog-inline-cta${editMode ? ' be-structured-block' : ''}`}>
      <EditableText
        tag="h3"
        className="blog-inline-cta__heading"
        value={block.heading || ''}
        placeholder="Eye-catching CTA heading"
        onSave={(v) => updateBlock(block.id, { heading: v })}
      />
      <EditableText
        tag="p"
        className="blog-inline-cta__body"
        value={block.body || ''}
        placeholder="Supporting copy that drives action…"
        multiline
        onSave={(v) => updateBlock(block.id, { body: v })}
      />
      {editMode ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <input
            className="be-field__input"
            value={block.buttonText || ''}
            onChange={(e) => updateBlock(block.id, { buttonText: e.target.value })}
            placeholder="Button text"
            style={{ flex: '1 1 140px', fontSize: 13, color: 'var(--seasons-color-midnight)', background: 'rgba(255,255,255,0.95)' }}
          />
          <input
            className="be-field__input"
            value={block.buttonHref || ''}
            onChange={(e) => updateBlock(block.id, { buttonHref: e.target.value })}
            placeholder="Button URL"
            style={{ flex: '2 1 200px', fontSize: 13, color: 'var(--seasons-color-midnight)', background: 'rgba(255,255,255,0.95)' }}
          />
        </div>
      ) : block.buttonHref ? (
        <a href={block.buttonHref} className="block-button-link" style={{ display: 'inline-block', marginTop: 8 }}>
          <div className="block-button block-button--md block-button--primary">
            <div className="block-button__text">
              <span className="btn-primary-text">{block.buttonText || 'Learn more'}</span>
              <span className="btn-hover-text">{block.buttonText || 'Learn more'}</span>
            </div>
          </div>
        </a>
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────
   TLDR SUMMARY
   ───────────────────────────────────────────── */
function TLDRBlock({ block }: { block: any }) {
  const { updateBlock, editMode } = useEditorCtx();
  return (
    <div className={`blog-tldr${editMode ? ' be-structured-block' : ''}`}>
      <div className="blog-tldr__head">
        <span className="blog-tldr__label">
          <span className="blog-tldr__label-text">TL;DR</span>
        </span>
      </div>
      <div className="blog-tldr__body">
        <EditableText
          tag="p"
          value={block.body || ''}
          placeholder="Quick summary of this article…"
          multiline
          onSave={(v) => updateBlock(block.id, { body: v })}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main dispatcher
   ───────────────────────────────────────────── */
export function BlockRenderer({ block }: { block: Block }) {
  switch (block.type) {
    case 'paragraph':      return <ParagraphBlock block={block} />;
    case 'lede':           return <LedeBlock block={block} />;
    case 'h2':
    case 'h3':
    case 'h4':             return <HeadingBlock block={block} />;
    case 'blockquote':     return <BlockquoteBlock block={block} />;
    case 'pullquote':      return <PullquoteBlock block={block} />;
    case 'ul':
    case 'ol':             return <ListBlock block={block} />;
    case 'code':           return <CodeBlock block={block} />;
    case 'hr':             return <HrBlock />;
    case 'BlogImage':      return <BlogImageBlock block={block} />;
    case 'BlogVideo':      return <BlogVideoBlock block={block} />;
    case 'ImageCarousel':  return <ImageCarouselBlock block={block} />;
    case 'ImageGrid':      return <ImageGridBlock block={block} />;
    case 'Callout':        return <CalloutBlock block={block} />;
    case 'ReadingCallout': return <ReadingCalloutBlock block={block} />;
    case 'Columns':        return <ColumnsBlock block={block} />;
    case 'StatsGrid':      return <StatsGridBlock block={block} />;
    case 'AuthorCard':     return <AuthorCardBlock block={block} />;
    case 'InlineCTA':      return <InlineCTABlock block={block} />;
    case 'TLDR':           return <TLDRBlock block={block} />;
    case 'BlogTable':      return <BlogTableBlock block={block} />;
    case 'rawHtml':        return <RawHtmlBlock block={block} />;
    default:               return null;
  }
}

function RawHtmlBlock({ block }: { block: Extract<Block, { type: 'rawHtml' }> }) {
  const { editMode, updateBlock } = useEditorCtx();
  const containerRef = useRef<HTMLDivElement>(null);

  // dangerouslySetInnerHTML renders HTML but never executes <script> tags.
  // After mount, replace every <script> with a fresh element so the browser
  // actually runs it — this enables Phosphor Icons, counter animations, etc.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const scripts = Array.from(container.querySelectorAll('script'));
    scripts.forEach(oldScript => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr =>
        newScript.setAttribute(attr.name, attr.value)
      );
      if (oldScript.textContent) newScript.textContent = oldScript.textContent;
      oldScript.parentNode?.replaceChild(newScript, oldScript);
    });
  }, [block.html]);

  if (!editMode) {
    // Strip Webflow spacer paragraphs that create unwanted vertical gaps.
    // Also inject stable `id` attributes into h2/h3 elements so the mobile TOC
    // can link to them. IDs are baked into the HTML string so they survive
    // React reconciliation (dangerouslySetInnerHTML re-uses the same string,
    // so the DOM is left untouched on re-renders unless the source HTML changes).
    let headingCount = 0;
    const cleanHtml = block.html
      .replace(/<p[^>]*>(?:&nbsp;|&#\d+;|\s|[\u200b-\u200d\ufeff\u00a0])*<\/p>/gi, "")
      // Strip editor-only attributes that can get baked into rawHtml content
      // (be-text-editable class, data-placeholder, contenteditable, editor box-shadows).
      .replace(/\s+class="be-text-editable"/gi, '')
      .replace(/\s+class="([^"]*)\bbe-text-editable\b([^"]*)"/gi, (_, before, after) => {
        const trimmed = `${before} ${after}`.trim().replace(/\s+/g, ' ');
        return trimmed ? ` class="${trimmed}"` : '';
      })
      .replace(/\s+data-placeholder="[^"]*"/gi, '')
      .replace(/\s+contenteditable(?:="[^"]*")?/gi, '')
      .replace(/box-shadow:\s*rgba\(5\s*,\s*59\s*,\s*238[^;]*;?\s*/gi, '')
      .replace(/<(h[23])(\s[^>]*)?\s*>/gi, (_match, level: string, attrs: string = '') => {
        if (/\bid\s*=/i.test(attrs)) return _match; // already has id
        const id = `rh-${block.id.slice(-8)}-${level}-${headingCount++}`;
        return `<${level}${attrs} id="${id}">`;
      });
    return (
      <div
        ref={containerRef}
        className="be-rawhtml-render"
        dangerouslySetInnerHTML={{ __html: cleanHtml }}
        suppressHydrationWarning
      />
    );
  }
  return (
    <div className="be-rawhtml-editor">
      <div className="be-rawhtml-label">Raw HTML (imported from Webflow)</div>
      <textarea
        className="be-code-textarea"
        value={block.html}
        onChange={(e) => updateBlock(block.id, { html: e.target.value })}
        rows={16}
        spellCheck={false}
      />
    </div>
  );
}
